import { useEffect, useMemo, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { SideSheet } from '@/components/overlay-panels'
import { Button } from '@/components/ui'
import { getCookie } from '@/lib/utils'

type WazzupPanelProps = {
  applicationId: number | null
  onClose: () => void
  open: boolean
}

type WazzupIframePayload = {
  error?: string
  iframeUrl?: string
  url?: string
}

type WazzupCreateEntityResponse = {
  error?: string
  id?: number
}

function buildIframePath(applicationId: number | null) {
  if (applicationId == null) {
    return '/wazzup/iframe/'
  }

  const search = new URLSearchParams({ application_id: String(applicationId) })
  return `/wazzup/iframe/?${search.toString()}`
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('application/json')) {
    throw new Error('Wazzup временно недоступен.')
  }

  return (await response.json()) as T
}

async function loadIframeUrl(applicationId: number | null) {
  const response = await fetch(buildIframePath(applicationId), {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  })
  const payload = await parseJsonResponse<WazzupIframePayload>(response)

  if (!response.ok) {
    throw new Error(payload.error || 'Не удалось открыть Wazzup.')
  }

  const frameUrl = payload.url || payload.iframeUrl
  if (!frameUrl) {
    throw new Error(payload.error || 'Не удалось загрузить чат Wazzup.')
  }

  return frameUrl
}

async function createApplicationFromWazzup(eventData: unknown) {
  const response = await fetch('/wazzup/create-entity/', {
    body: JSON.stringify(eventData),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': getCookie('csrftoken'),
    },
    method: 'POST',
  })
  const payload = await parseJsonResponse<WazzupCreateEntityResponse>(response)

  if (!response.ok || !payload.id) {
    throw new Error(payload.error || 'Не удалось создать заявку из Wazzup.')
  }

  return payload.id
}

export function WazzupPanel({
  applicationId,
  onClose,
  open,
}: WazzupPanelProps) {
  const navigate = useNavigate()
  const [iframeUrl, setIframeUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const requestKey = useMemo(
    () => (applicationId == null ? 'global' : `application:${applicationId}`),
    [applicationId],
  )

  const openContextLabel =
    applicationId == null ? 'Рабочие диалоги с клиентами.' : 'Чат и карточка клиента для текущей заявки.'

  useEffect(() => {
    if (!open) {
      return
    }

    let isCancelled = false
    setIsLoading(true)
    setErrorMessage(null)

    void loadIframeUrl(applicationId)
      .then((nextIframeUrl) => {
        if (isCancelled) {
          return
        }
        setIframeUrl(nextIframeUrl)
      })
      .catch((error: unknown) => {
        if (isCancelled) {
          return
        }
        const message = error instanceof Error ? error.message : 'Не удалось открыть Wazzup.'
        setIframeUrl(null)
        setErrorMessage(message)
        toast.error(message)
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [applicationId, open, requestKey])

  useEffect(() => {
    if (!open) {
      return
    }

    const handleMessage = (event: MessageEvent) => {
      const payload = event.data
      if (!payload || typeof payload !== 'object') {
        return
      }

      const data = payload as {
        data?: { entity?: { id?: number | string } }
        type?: string
      }

      if (data.type === 'WZ_OPEN_ENTITY') {
        const entityId = data.data?.entity?.id
        if (!entityId) {
          return
        }

        onClose()
        navigate(`/application/${entityId}/`)
        return
      }

      if (data.type !== 'WZ_CREATE_ENTITY') {
        return
      }

      setIsSubmitting(true)
      void createApplicationFromWazzup(payload)
        .then((nextApplicationId) => {
          onClose()
          navigate(`/application/${nextApplicationId}/`)
        })
        .catch((error: unknown) => {
          const message =
            error instanceof Error ? error.message : 'Не удалось создать заявку из Wazzup.'
          toast.error(message)
        })
        .finally(() => {
          setIsSubmitting(false)
        })
    }

    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [navigate, onClose, open])

  const reloadPanel = () => {
    setIframeUrl(null)
    setErrorMessage(null)
    setIsLoading(true)

    void loadIframeUrl(applicationId)
      .then((nextIframeUrl) => {
        setIframeUrl(nextIframeUrl)
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : 'Не удалось открыть Wazzup.'
        setErrorMessage(message)
        toast.error(message)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  return (
    <SideSheet
      description={openContextLabel}
      onClose={onClose}
      open={open}
      title="Wazzup"
      widthClassName="max-w-5xl"
    >
      <div className="space-y-4">
        {errorMessage ? (
          <div className="rounded-[24px] border border-rose-100 bg-rose-50 p-5">
            <p className="text-sm font-medium text-rose-700">{errorMessage}</p>
            <div className="mt-4">
              <Button onClick={reloadPanel} tone="ghost">
                <RefreshCw className="mr-2 h-4 w-4" />
                Повторить
              </Button>
            </div>
          </div>
        ) : null}

        <div className="relative overflow-hidden rounded-[28px] border border-white/70 bg-white shadow-soft">
          {(isLoading || isSubmitting) ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-100/85 backdrop-blur-sm">
              <div className="flex items-center gap-3 rounded-full bg-white px-5 py-3 text-sm font-semibold text-ink-700 shadow-soft">
                <RefreshCw className="h-4 w-4 animate-spin" />
                {isSubmitting ? 'Создаем заявку...' : 'Открываем Wazzup...'}
              </div>
            </div>
          ) : null}

          {iframeUrl ? (
            <iframe
              allow="microphone *; clipboard-write *"
              className="h-[calc(100vh-220px)] min-h-[620px] w-full border-0"
              src={iframeUrl}
              title="Wazzup"
            />
          ) : (
            <div className="flex h-[calc(100vh-220px)] min-h-[620px] items-center justify-center px-6 text-center text-sm text-ink-500">
              Подготовим чат сразу после подключения.
            </div>
          )}
        </div>
      </div>
    </SideSheet>
  )
}
